import React, { useState, useMemo, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import SearchResults from "./SearchResults";
import { fetchFlights, getLastRoute } from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import "react-datepicker/dist/react-datepicker.css";
import SwapIcon from "../../assets/images/swap.png";
import AirportField from "./AirportField";
import DateRangeCalendar from "./DateRangeCalendar";
import NearbyAirportsToggle from "./NearbyAirportsToggle";
import MobileStopsFilter from "./MobileStopsFilter";
import RoutePager from "./RoutePager";
import { useAirportCodes } from "./useAirportCodes";
import {
  mapStopsFilterToParams,
  withDirectFlightCompositeIds,
  withConnectionCompositeIds,
} from "./routeSearchHelpers";
import {
  formatDateLocal,
  saveLastSearchToStorage,
  loadLastSearchFromStorage,
  saveRecentLocation,
  loadRecentLocations,
  RecentLocation,
  LastSearchData,
} from "./searchStorage";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
  #datepicker-portal .react-datepicker-popper {
    z-index: 99999 !important;
  }
`;

interface LastRouteResponse {
  lastSearchFlightNumber: string | null;
  lastSearchRoute: {
    source: string;
    destination: string;
    date: string;
  };
}
interface SearchByRouteProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: (optimisticItems?: any[]) => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
}

const SearchByRoute: React.FC<SearchByRouteProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const [selectedDeparture, setSelectedDeparture] = useState<"yes" | "no">(
    "no",
  );
  const [selectedArrival, setSelectedArrival] = useState<"yes" | "no">("no");
  const [stopsFilter, setStopsFilter] = useState<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  const [fromLocation, setFromLocation] = useState<string>("");
  const [toLocation, setToLocation] = useState<string>("");
  const [date, setDate] = useState<string>(
    formatDateLocal(new Date(Date.now() + 86400000)),
  ); // Default to Tomorrow's date
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState<{
    from?: string;
    to?: string;
    date?: string;
  }>({});
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<
    ConnectionFlight[]
  >([]);
  const [isLoadingFlights, setIsLoadingFlights] = useState(false);
  const [isLoadingLastSearch, setIsLoadingLastSearch] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const [hasSearched, setHasSearched] = useState(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Recent locations state
  const [recentFromLocations, setRecentFromLocations] = useState<
    RecentLocation[]
  >([]);
  const [recentToLocations, setRecentToLocations] = useState<RecentLocation[]>(
    [],
  );
  const [isFromCardOpen, setIsFromCardOpen] = useState<boolean>(false);
  const [isToCardOpen, setIsToCardOpen] = useState<boolean>(false);
  const [fromSearchInput, setFromSearchInput] = useState<string>("");
  const [toSearchInput, setToSearchInput] = useState<string>("");

  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  // Create options for react-select with combined code and name - memoized to prevent re-renders
  // Airport autocomplete data (From/To fields).
  const {
    departureOptions,
    arrivalOptions,
    isLoadingDepartures,
    isLoadingArrivals,
    loadDepartureCodes,
    loadArrivalCodes,
    resetArrivalSearch,
  } = useAirportCodes(fromLocation, toLocation);

  // Load recent locations from localStorage on component mount
  useEffect(() => {
    const fromLocs = loadRecentLocations("from");
    const toLocs = loadRecentLocations("to");
    setRecentFromLocations(fromLocs);
    setRecentToLocations(toLocs);
  }, []);

  // Resolve the airport name for a code from its options list or recent history.
  const resolveLocationName = (
    code: string,
    options: { value: string; label: string }[],
    recents: RecentLocation[],
  ): string => {
    if (!code) return "";
    const option = options.find((o) => o.value === code);
    if (option) {
      const dashIdx = option.label.indexOf(" - ");
      return dashIdx >= 0 ? option.label.slice(dashIdx + 3) : option.label;
    }
    return recents.find((r) => r.code === code)?.name || "";
  };

  // Display text for a field's read-only input ("CODE - Name", or bare code).
  const resolveFieldDisplay = (
    code: string,
    options: { value: string; label: string }[],
    recents: RecentLocation[],
  ): string => {
    if (!code) return "";
    const option = options.find((o) => o.value === code);
    if (option) return option.label;
    const recent = recents.find((l) => l.code === code);
    return recent ? `${recent.code} - ${recent.name}` : code;
  };

  // Selecting a From airport: persist it, clear conflicts, advance to "To".
  const handleSelectFrom = (code: string, name: string) => {
    setFromLocation(code);
    setErrors((prev) => ({ ...prev, from: undefined }));
    saveRecentLocation("from", code, name);
    setRecentFromLocations(loadRecentLocations("from"));
    if (code === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
    setIsFromCardOpen(false);
    setFromSearchInput("");
    setTimeout(() => setIsToCardOpen(true), 100);
  };

  // Selecting a To airport: persist it, clear conflicts, open the calendar.
  const handleSelectTo = (code: string, name: string) => {
    setToLocation(code);
    setErrors((prev) => ({ ...prev, to: undefined }));
    saveRecentLocation("to", code, name);
    setRecentToLocations(loadRecentLocations("to"));
    if (code === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
    setIsToCardOpen(false);
    setToSearchInput("");
    resetArrivalSearch();
    setTimeout(() => setIsCalendarOpen(true), 100);
  };

  // Swap the Flying From / Going to selections.
  const handleSwapLocations = () => {
    if (!fromLocation && !toLocation) return;

    const newFromCode = toLocation;
    const newToCode = fromLocation;
    const newFromName = resolveLocationName(
      toLocation,
      arrivalOptions,
      recentToLocations,
    );
    const newToName = resolveLocationName(
      fromLocation,
      departureOptions,
      recentFromLocations,
    );

    setFromLocation(newFromCode);
    setToLocation(newToCode);
    setFromSearchInput(toSearchInput);
    setToSearchInput(fromSearchInput);

    // Persist names under the new field type so the swapped codes still render
    // their full "CODE - Name" labels instead of a bare code.
    if (newFromCode && newFromName) {
      saveRecentLocation("from", newFromCode, newFromName);
      setRecentFromLocations(loadRecentLocations("from"));
    }
    if (newToCode && newToName) {
      saveRecentLocation("to", newToCode, newToName);
      setRecentToLocations(loadRecentLocations("to"));
    }

    setErrors((prev) => ({ ...prev, from: undefined, to: undefined }));
  };

  // Clear "To" field if same value is selected in "From"
  useEffect(() => {
    if (fromLocation && fromLocation === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [fromLocation]);

  // Clear "From" field if same value is selected in "To"
  useEffect(() => {
    if (toLocation && toLocation === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [toLocation]);

  // Track previous filter value to detect changes
  const prevStopsFilter = useRef<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  // Track previous page number to detect page changes
  const prevPageNumber = useRef<number>(1);

  // Handler for stops filter change - sets loading state immediately
  const handleStopsFilterChange = (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => {
    // Always set the filter
    const filterChanged = hasSearched && prevStopsFilter.current !== filter;

    // If filter changed and user has searched, set loading state immediately (synchronously)
    if (filterChanged) {
      setIsLoadingFlights(true);
    }

    // Update the filter which will trigger the useEffect
    setStopsFilter(filter);
  };

  // Fetch flights when filter changes (reset to page 1 first)
  useEffect(() => {
    // Only fetch if filter actually changed and user has searched before
    if (hasSearched && prevStopsFilter.current !== stopsFilter) {
      // Reset to page 1 if needed - update prevPageNumber to prevent page change effect
      const needsPageReset = PageNumber !== 1;
      if (needsPageReset) {
        prevPageNumber.current = PageNumber; // Mark that we're resetting, not changing
        setPageNumber(1);
      }

      // Use requestAnimationFrame to ensure React has rendered the skeleton before starting API call
      const fetchWithFilter = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for filter:", error);
        }
      };

      fetchWithFilter();
    }
    // Update previous filter value after checking
    if (hasSearched) {
      prevStopsFilter.current = stopsFilter;
    }
  }, [stopsFilter, hasSearched, PageNumber]);

  // Handler for page number change - sets loading state immediately
  const handlePageChange = (page: number) => {
    if (hasSearched && PageNumber !== page) {
      // Set loading state immediately - this happens synchronously
      setIsLoadingFlights(true);
      // Then update the page number which will trigger the useEffect
      setPageNumber(page);
    }
  };

  // Fetch flights when page number changes (but not on initial load or filter change)
  useEffect(() => {
    // Only fetch if page actually changed (not initial load or filter reset)
    if (
      hasSearched &&
      prevPageNumber.current !== PageNumber &&
      prevPageNumber.current !== null
    ) {
      const fetchFlightsForPage = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Update previous page number after checking
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // No auto-reset of filter — if no results found, show empty state instead of silently switching.

  // Clear search function - resets all fields and search state
  const handleClearSearch = () => {
    // Clear form fields
    setFromLocation("");
    setToLocation("");
    setSelectedDates([]);
    setDatesInRange([]);
    setDate(formatDateLocal(new Date(Date.now() + 86400000))); // Reset to tomorrow

    // Close cards and clear search inputs
    setIsFromCardOpen(false);
    setIsToCardOpen(false);
    setFromSearchInput("");
    setToSearchInput("");

    // Clear search results
    setFlights([]);
    setConnectionFlights([]);
    setTotalFlights(0);
    setShowResults(false);

    // Reset search state
    setHasSearched(false);
    setPageNumber(1);
    prevPageNumber.current = 1;
    prevStopsFilter.current = "none";

    // Reset filters
    setStopsFilter("none");
    setSelectedDeparture("no");
    setSelectedArrival("no");

    // Clear errors
    setErrors({});

    // Hide results
    onShowSearchResults?.(false);
  };

  // Optimized last search function - loads from localStorage first, falls back to API
  const handleLastSearchClick = async () => {
    setIsLoadingLastSearch(true);
    setIsLoadingFlights(true); // Show skeleton for search

    try {
      // First check localStorage
      let lastSearchData: LastSearchData | null = loadLastSearchFromStorage();

      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const response = await getLastRoute();
          const lastRouteData: LastRouteResponse = response.data || response;

          if (lastRouteData && lastRouteData.lastSearchRoute) {
            const {
              source,
              destination,
              date: lastDate,
            } = lastRouteData.lastSearchRoute;

            // Convert single date to array format for storage
            lastSearchData = {
              source,
              destination,
              dates: [lastDate],
              savedAt: Date.now(),
            };

            // Save to localStorage for future use
            saveLastSearchToStorage(source, destination, [lastDate]);
          } else {
            hideLoader();
            setIsLoadingLastSearch(false);
            setIsLoadingFlights(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          setIsLoadingLastSearch(false);
          setIsLoadingFlights(false);
          return;
        } finally {
          hideLoader();
        }
      }

      // Populate fields with stored data
      if (lastSearchData) {
        const { source, destination, dates } = lastSearchData;

        // Set locations
        setFromLocation(source);
        setToLocation(destination);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates
          .map((dateStr) => {
            const date = new Date(dateStr);
            // Validate date
            if (isNaN(date.getTime())) {
              return null;
            }
            return date;
          })
          .filter((date): date is Date => date !== null);

        // Set dates
        setSelectedDates(dateObjects);
        setDatesInRange(dates);

        // Set date state for backward compatibility (use first date if available)
        if (dates.length > 0) {
          setDate(dates[0]);
        }

        // Clear any errors
        setErrors({});

        // Set search state
        setHasSearched(true);
        onShowSearchResults?.(true);
        prevPageNumber.current = 1;
        setPageNumber(1);

        // A new search starts fresh at the default (non-stop) filter so a
        // previously selected "Connected"/stops filter doesn't carry over.
        setStopsFilter("none");
        prevStopsFilter.current = "none";

        // Wait for state updates to complete, then auto-execute search with direct values
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resolve(undefined);
            });
          });
        });

        // Call getFlights with direct values to ensure they're used.
        // Pass the reset filter explicitly since setStopsFilter above is async.
        await getFlights({
          source,
          destination,
          dates,
          pageNumber: 1,
          stopsFilter: "none",
        });

        // Scroll to search results
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading last search:", error);
    } finally {
      setIsLoadingLastSearch(false);
      // Note: isLoadingFlights will be set to false by getFlights() finally block
    }
  };

  const getFlights = async (overrideParams?: {
    source?: string;
    destination?: string;
    dates?: string[];
    pageNumber?: number;
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
  }) => {
    // Set loading state - will be reset in finally block
    setIsLoadingFlights(true);
    try {
      // Map stops filter to API parameters
      const { Stops, IncludeDirect, IncludeConnections } =
        mapStopsFilterToParams(overrideParams?.stopsFilter ?? stopsFilter);

      // Use override params if provided, otherwise use state values
      const sourceValue = overrideParams?.source ?? fromLocation;
      const destinationValue = overrideParams?.destination ?? toLocation;
      const datesValue = overrideParams?.dates ?? datesInRange;
      const pageNumberValue = overrideParams?.pageNumber ?? PageNumber;

      // Prepare params as plain object
      const params = {
        Source: sourceValue,
        Destination: destinationValue,
        IsNearbyDeparture: selectedDeparture === "yes",
        IsNearbyArrival: selectedArrival === "yes",
        Dates: datesValue,
        PageNumber: pageNumberValue,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };

      // Fetch flights (GET request)
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections ||
        []) as ConnectionFlight[];

      // If we requested connections but API returned only direct flights,
      // still show results rather than silently reverting the filter.

      const flightData = rawFlightData as FlightDetails[];

      const flightsWithCompositeIds = withDirectFlightCompositeIds(flightData);
      const connectionsWithCompositeIds =
        withConnectionCompositeIds(connectionsData);

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // Total = direct single-flight results (data.total) + multi-leg
      // connections (connectionTotal). A 1/2/3-stop search returns BOTH — e.g. a
      // single-flight-number 1-stop like AI101 in `items`, PLUS multi-airline
      // connections — so the count is the sum. Non-stop has only `total`,
      // Connected only `connectionTotal`; the missing side counts as 0.
      const directTotal = response.data.total ?? 0;
      const connTotal = response.data.connectionTotal ?? 0;

      if (
        response.data.total !== undefined ||
        response.data.connectionTotal !== undefined
      ) {
        setTotalFlights(directTotal + connTotal);
      } else {
        // Fallback: estimate based on current page results
        const estimatedTotal =
          flightData.length + connectionsData.length < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage +
              flightData.length +
              connectionsData.length
            : PageNumber * flightsPerPage;
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
    } catch (err: any) {
      const apiMessage = err.response?.data?.message;
      const status = err.response?.status;
      // "No flights found" is a valid empty result, not an error — the inline
      // empty state already communicates it, so don't also raise a red toast.
      const isNoResults =
        status === 404 ||
        (typeof apiMessage === "string" &&
          /no flights?\s+found|no\s+results?/i.test(apiMessage));
      if (!isNoResults) {
        useToastStore
          .getState()
          .showToast(apiMessage || "Failed to fetch flights.", "error");
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    // Simple validation check when the search button is clicked
    const newErrors: typeof errors = {};
    if (!fromLocation) newErrors.from = "*Please select a departure location.";
    if (!toLocation) newErrors.to = "*Please select an arrival location.";
    if (selectedDates.length === 0)
      newErrors.date = "*Please select at least one date.";
    if (fromLocation === toLocation && fromLocation) {
      newErrors.from = "Departure and arrival locations cannot be the same.";
      newErrors.to = "Departure and arrival locations cannot be the same.";
    }
    setErrors(newErrors);

    // Only show the search results area when validation passes
    if (Object.keys(newErrors).length > 0) return;

    setHasSearched(true);
    onShowSearchResults?.(true);
    prevPageNumber.current = 1; // Reset prev page number for new search
    setPageNumber(1); // Reset to first page on new search

    // A new search starts fresh at the default (non-stop) filter — otherwise a
    // previously selected "Connected"/stops filter would persist. Sync the ref
    // too so the stopsFilter-change effect doesn't fire a redundant refetch.
    setStopsFilter("none");
    prevStopsFilter.current = "none";

    try {
      // Set loading state first to show skeleton
      setIsLoadingFlights(true);
      // Small delay to ensure skeleton renders
      await new Promise((resolve) => setTimeout(resolve, 50));
      // Pass the reset filter explicitly since setStopsFilter above is async.
      await getFlights({ stopsFilter: "none" });

      // Save search data to localStorage after successful search
      if (fromLocation && toLocation && datesInRange.length > 0) {
        saveLastSearchToStorage(fromLocation, toLocation, datesInRange);
      }

      // Scroll to search results after a brief delay to ensure content is rendered
      setTimeout(() => {
        const searchResultsElement = document.getElementById("search-results");
        if (searchResultsElement) {
          searchResultsElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (error) {
      console.error("Error fetching flights:", error);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr,
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex,
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime(),
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = async () => {
    const formattedDates = selectedDates.map((d) => formatDateLocal(d));
    setDatesInRange(formattedDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled
    if (fromLocation && toLocation && selectedDates.length > 0) {
      setTimeout(async () => {
        try {
          // Set search state
          setHasSearched(true);
          onShowSearchResults?.(true);
          prevPageNumber.current = 1;
          setPageNumber(1);

          // A new search starts fresh at the default (non-stop) filter —
          // otherwise a previously selected "Connected"/stops filter would
          // persist. This path auto-searches on date save (before the "Find
          // Your Flight" click), so it must reset just like handleSearchClick.
          setStopsFilter("none");
          prevStopsFilter.current = "none";

          // Clear any previous errors
          setErrors({});

          // Set loading state first to show skeleton
          setIsLoadingFlights(true);
          // Small delay to ensure skeleton renders
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Call getFlights with override params to ensure dates are passed.
          // Pass the reset filter explicitly since setStopsFilter above is async.
          await getFlights({
            source: fromLocation,
            destination: toLocation,
            dates: formattedDates,
            pageNumber: 1,
            stopsFilter: "none",
          });

          // Save search data to localStorage after successful search
          saveLastSearchToStorage(fromLocation, toLocation, formattedDates);

          // Scroll to search results after a brief delay to ensure content is rendered
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    return (
      <div id="search-results">
        {/* Mobile: back + filter bar */}
        <div className="flex items-center justify-between md:hidden mt-4 mb-2 px-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors"
              onClick={() => {
                handleClearSearch();
                onBack?.();
              }}
            >
              <svg className="w-4 h-4 text-[#1F2937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
            <span className="text-gray-400 text-xs">&gt;</span>
            <span className="text-[#1F2937] text-sm font-medium">Route</span>
          </div>
          <button
            type="button"
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </button>
        </div>

        {/* Mobile: results count */}
        <p className="md:hidden text-[#6B7280] text-xs mb-3 px-1">
          Showing {Number(totalFlights) || flights.length + connectionFlights.length} results
        </p>

        {/* Mobile Filter Drawer */}
        <MobileStopsFilter
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          resultCount={
            Number(totalFlights) || flights.length + connectionFlights.length
          }
          readOnly={readOnly}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          hideThreeStops
        />

        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={(items) => {
            // Return to the home/recent view, then hand the posted cards up.
            handleClearSearch();
            onPostRequestSuccess?.(items);
          }}
          sourceLabel={fromLocation}
          destinationLabel={toLocation}
          totalResults={totalFlights}
          hideInternalFilters={true}
          hideConnectedFilter={readOnly}
          readOnly={readOnly}
        />
        <RoutePager
          page={PageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
    showMobileFilters,
  ]);

  const filterSidebar = (
    <div
      className={`w-[220px] flex-shrink-0 ${hasSearched ? "hidden md:block" : "hidden"}`}
    >
      <div className="sticky top-4">
        {/* Breadcrumb — outside card */}
        <div className="flex items-center gap-3 mb-4 whitespace-nowrap">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors flex-shrink-0"
            onClick={() => {
              handleClearSearch();
              onBack?.();
            }}
          >
            <svg
              className="w-4 h-4 text-[#1F2937]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
          <span className="text-gray-400 text-sm">&gt;</span>
          <span className="text-[#1F2937] text-sm font-medium">Route</span>
        </div>

        {/* Filter card */}
        <div className="bg-white rounded-2xl shadow-sm p-5 min-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[#1F2937] text-base font-bold">Filters</p>
            <button
              type="button"
              className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              title="Reset filters"
              onClick={() => handleStopsFilterChange("none")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p className="text-[#9CA3AF] text-xs mb-4">
            Showing {Number(totalFlights) || flights.length + connectionFlights.length} results
          </p>
          <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
          <div className="flex flex-col gap-3">
            {/* "three" (3 Stops) hidden on the Route search — commented out. */}
            {(readOnly
              ? (["none", "one", "two" /*, "three"*/] as const)
              : (["none", "one", "two" /*, "three"*/, "one-or-fewer"] as const)
            ).map(
              (val) => {
                const labels: Record<string, string> = {
                  none: "Non-stop",
                  one: "1 Stop",
                  two: "2 Stops",
                  three: "3 Stops",
                  "one-or-fewer": "Connected",
                };
                return (
                  <label
                    key={val}
                    className="flex items-center cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops-route"
                        checked={stopsFilter === val}
                        onChange={() => handleStopsFilterChange(val)}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        {labels[val]}
                      </span>
                    </div>
                  </label>
                );
              },
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      {/* Tabs + Form — always default style, full width (no layout change on search) */}
      <div className="relative pt-4">
        <div className="relative z-10">{tabButtons}</div>
        <div className="relative z-20 bg-white shadow-xl p-3 overflow-visible rounded-2xl rounded-tl-none">
              <div className="flex items-center justify-between">
                <p className="text-[#1F2937] text-sm font-medium">
                  Search by Route
                </p>
                <div className="flex items-center gap-3">
                  <button
                    className="flex items-center gap-1 text-[#6B7280] hover:text-[#4B5563] transition-colors"
                    onClick={handleClearSearch}
                    type="button"
                  >
                    <i className="st-close text-xs"></i>
                    <span className="text-xs">Clear</span>
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFFBEB] border border-[#F8F8F8] rounded-lg hover:bg-[#FEF3C7] transition-colors"
                    onClick={handleLastSearchClick}
                    disabled={isLoadingLastSearch}
                    type="button"
                  >
                    <i className="st-previous text-[#192227] text-xs"></i>
                    <span className="font-semibold text-[#192227] text-xs">
                      {isLoadingLastSearch ? "Loading..." : "Last Search"}
                    </span>
                  </button>
                </div>
              </div>
              <div>
                {/* Main fields row: From+To in one box, Date in separate box */}
                <div className="flex flex-col md:flex-row md:items-stretch gap-4 my-2 overflow-visible">
                  {/* From + To container. Divider between the sections shows on
                      mobile only (stacked); no divider on desktop (side by side). */}
                  <div className="flex flex-col md:flex-row md:items-stretch flex-[2] border border-[#E5E7EB] rounded-xl overflow-visible divide-y md:divide-y-0 divide-[#E5E7EB]">
                    {/* From Field */}
                    <AirportField
                      type="from"
                      label="Flying From ?"
                      selectedCode={fromLocation}
                      displayValue={resolveFieldDisplay(
                        fromLocation,
                        departureOptions,
                        recentFromLocations,
                      )}
                      options={departureOptions}
                      recentLocations={recentFromLocations}
                      isOpen={isFromCardOpen}
                      setOpen={setIsFromCardOpen}
                      searchInput={fromSearchInput}
                      setSearchInput={setFromSearchInput}
                      isLoading={isLoadingDepartures}
                      onSearch={loadDepartureCodes}
                      onSelect={handleSelectFrom}
                      onClear={() => {
                        setFromLocation("");
                        setIsFromCardOpen(false);
                        setFromSearchInput("");
                      }}
                      onClose={() => {
                        setIsFromCardOpen(false);
                        setFromSearchInput("");
                      }}
                      error={errors.from}
                    />

                    {/* Swap icon */}
                    <div className="flex items-center justify-center px-1">
                      <button
                        type="button"
                        onClick={handleSwapLocations}
                        aria-label="Swap departure and arrival"
                        className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors p-1 cursor-pointer"
                      >
                        <img src={SwapIcon} alt="Swap" className="w-7 h-7" />
                      </button>
                    </div>

                    {/* To Field */}
                    <AirportField
                      type="to"
                      label="Going to ?"
                      selectedCode={toLocation}
                      displayValue={resolveFieldDisplay(
                        toLocation,
                        arrivalOptions,
                        recentToLocations,
                      )}
                      options={arrivalOptions}
                      recentLocations={recentToLocations}
                      isOpen={isToCardOpen}
                      setOpen={setIsToCardOpen}
                      searchInput={toSearchInput}
                      setSearchInput={setToSearchInput}
                      isLoading={isLoadingArrivals}
                      onSearch={loadArrivalCodes}
                      onSelect={handleSelectTo}
                      onClear={() => {
                        setToLocation("");
                        setIsToCardOpen(false);
                        setToSearchInput("");
                      }}
                      onClose={() => {
                        setIsToCardOpen(false);
                        setToSearchInput("");
                        resetArrivalSearch();
                      }}
                      error={errors.to}
                    />
                  </div>

                  {/* Date field - separate bordered box */}
                  <DateRangeCalendar
                    selectedDates={selectedDates}
                    datesInRange={datesInRange}
                    isCalendarOpen={isCalendarOpen}
                    setIsCalendarOpen={setIsCalendarOpen}
                    onDateChange={handleDateChange}
                    onSaveDates={handleSaveDates}
                    onClearDates={clearAllDates}
                    error={errors.date}
                    popperPlacement="bottom-end"
                    portalId="datepicker-portal"
                  />
                </div>

                {/* Include nearby toggles + Find button row */}
                <div className="flex flex-wrap items-stretch justify-between gap-3">
                  {!readOnly && (
                    <div className="flex flex-col md:flex-row md:flex-wrap items-stretch gap-3 w-full md:w-auto">
                      <NearbyAirportsToggle
                        label="Departure Airports"
                        enabled={selectedDeparture === "yes"}
                        onToggle={() =>
                          setSelectedDeparture(
                            selectedDeparture === "yes" ? "no" : "yes",
                          )
                        }
                      />
                      <NearbyAirportsToggle
                        label="Arrival Airports"
                        enabled={selectedArrival === "yes"}
                        onToggle={() =>
                          setSelectedArrival(
                            selectedArrival === "yes" ? "no" : "yes",
                          )
                        }
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    className={`w-full md:w-auto self-center px-8 py-3 rounded-xl text-[#192227] text-center bg-[#FBBF24] hover:bg-[#F59E0B] text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors ${
                      isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    onClick={handleSearchClick}
                    disabled={isLoadingFlights}
                  >
                    {isLoadingFlights ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#192227] border-t-transparent rounded-full animate-spin"></div>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        Find Your Flight
                      </>
                    )}
                  </button>
                </div>
              </div>
        </div>
      </div>

      {/* Search results with filter sidebar — rendered below the search area */}
      <div className="flex gap-4 pt-2 md:pt-8">
        {filterSidebar}
        <div className="flex-1 min-w-0">{searchResults}</div>
      </div>
    </div>
  );
};

export default SearchByRoute;
